import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import StatusBadge from "../../components/UI/StatusBadge";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";

export default function MyApprovers() {
  const { roles } = useContext(AuthContext);
  const token = getToken();
  const [notification, setNotification] = useState(null);

  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [approversData, setApproversData] = useState([]);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  const fetchMyApprovers = async () => {
    setLoadingApprovers(true);
    try {
      const response = await axios.get(
        "https://hrmspayroll.prepstripe.com/api/approval/my-approvers",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setApproversData(response?.data?.data?.data || []);
    } catch (error) {
      setNotification({
        message:
          error?.response?.data?.error || "Error fetching approvers data",
        type: "error",
      });
    } finally {
      setLoadingApprovers(false);
    }
  };

  useEffect(() => {
    fetchMyApprovers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 rounded-lg">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <nav className="flex flex-wrap mb-4 font-poppins" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 text-md font-medium text-gray-500 md:ms-2">
                My Approvers
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="flex justify-between items-center mb-4 font-poppins">
        <h3 className="text-lg font-semibold dark:text-white">My Approvers</h3>
        <button
          onClick={fetchMyApprovers}
          disabled={loadingApprovers}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 font-medium text-white px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw
            className={`w-4 h-4 ${loadingApprovers ? "animate-spin" : ""}`}
          />
          {loadingApprovers ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
        <table className="min-w-full table-auto text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-3 px-4 text-center text-xs md:text-sm text-black dark:text-white whitespace-nowrap font-semibold">
                Sr No.
              </th>
              <th className="py-3 px-4 text-center text-xs md:text-sm text-black dark:text-white whitespace-nowrap font-semibold">
                Approval Type
              </th>
              <th className="py-3 px-4 text-center text-xs md:text-sm text-black dark:text-white whitespace-nowrap font-semibold">
                Approval Level
              </th>
              <th className="py-3 px-4 text-center text-xs md:text-sm text-black dark:text-white whitespace-nowrap font-semibold">
                Primary Approver
              </th>
              <th className="py-3 px-4 text-center text-xs md:text-sm text-black dark:text-white whitespace-nowrap font-semibold">
                Secondary Approver
              </th>
              <th className="py-3 px-4 text-center text-xs md:text-sm text-black dark:text-white whitespace-nowrap font-semibold">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {approversData.length > 0 ? (
              approversData.map((approval, index) => (
                <tr
                  key={approval.id}
                  className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-white transition-colors"
                >
                  <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap text-center font-medium">
                    {index + 1}
                  </td>
                  <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap text-center">
                    {approval.approval_module_id || "-"}
                  </td>
                  <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap text-center">
                    {approval.approval_level_id || "-"}
                  </td>
                  <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap text-center">
                    {approval.primary_approver_id || "-"}
                  </td>
                  <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap text-center">
                    {approval.secondary_approver_id || "-"}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-center">
                    <StatusBadge
                      status={
                        approval.status === "active" ||
                        approval.is_active === 1 ||
                        approval.is_active === true
                          ? "active"
                          : "inactive"
                      }
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="py-4 px-4 text-center text-gray-500 dark:text-gray-400"
                  colSpan={6}
                >
                  {loadingApprovers
                    ? "Loading approvers..."
                    : "No approvers found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
